// IndexedDB 관리자
// app.js의 요청에 의해 데이터를 저장하거나 로드하는 역할만 담당

const DB_NAME = 'KurcAppDB';
const DB_VERSION = 2;

let cachedDBPromise = null;

// IndexedDB 초기화 (싱글톤 커넥션 캐시)
const initDB = () => {
    if (cachedDBPromise) return cachedDBPromise;
    
    cachedDBPromise = new Promise((resolve, reject) => {
        const request = indexedDB.open(DB_NAME, DB_VERSION);

        request.onerror = () => reject(request.error);
        request.onsuccess = () => {
            const db = request.result;
            db.onversionchange = () => {
                try { db.close(); } catch (e) {}
                cachedDBPromise = null;
            };
            resolve(db);
        };

        request.onupgradeneeded = (event) => {
            const db = event.target.result;
            createStores(db);
        };
    });

    return cachedDBPromise;
};

// 스토어 생성
const createStores = (db) => {
    // 로그 데이터 스토어
    if (!db.objectStoreNames.contains('logs')) {
        const logsStore = db.createObjectStore('logs', { keyPath: 'id' });
        logsStore.createIndex('createdAt', 'createdAt', { unique: false });
    }
    
    // 작업 데이터 스토어 (tasks)
    if (!db.objectStoreNames.contains('tasks')) {
        const tasksStore = db.createObjectStore('tasks', { keyPath: 'id' });
        tasksStore.createIndex('updatedAt', 'updatedAt', { unique: false });
    }
    
    // 작업 이력 스토어 (works)
    if (!db.objectStoreNames.contains('works')) {
        const worksStore = db.createObjectStore('works', { keyPath: 'id' });
        worksStore.createIndex('createdAt', 'createdAt', { unique: false });
    }
    
    // 마지막 업데이트 시간 스토어
    if (!db.objectStoreNames.contains('updateTimes')) {
        db.createObjectStore('updateTimes', { keyPath: 'dataType' });
    }
};

// IDBRequest -> Promise 래퍼
const requestToPromise = (request) => new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
});

// 트랜잭션 완료 대기
const waitTransactionComplete = (tx) => new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error);
});

// 로그 데이터 저장
export const saveLogs = async (logs) => {
    const db = await initDB();
    const transaction = db.transaction(['logs'], 'readwrite');
    const store = transaction.objectStore('logs');
    
    // 기존 데이터 삭제 후 새 데이터 저장
    await requestToPromise(store.clear());
    await Promise.all(logs.map(log => requestToPromise(store.put(log))));
    await waitTransactionComplete(transaction);
    
    console.log('[INDEXEDDB] 로그 데이터 저장 완료:', logs.length);
    
    // 마지막 업데이트 시간 저장: 저장된 로그의 최대 createdAt 기준
    const latest = logs.reduce((acc, cur) => {
        const ts = cur && cur.createdAt;
        return (!acc || (ts && ts > acc)) ? ts : acc;
    }, null) || new Date().toISOString();
    await saveUpdateTime('logs', latest);
};

// 작업 데이터 저장 (tasks)
export const saveTasks = async (tasks) => {
    const db = await initDB();
    const transaction = db.transaction(['tasks'], 'readwrite');
    const store = transaction.objectStore('tasks');
    
    await requestToPromise(store.clear());
    await Promise.all(tasks.map(task => requestToPromise(store.put(task))));
    await waitTransactionComplete(transaction);
    
    console.log('[INDEXEDDB] 작업 데이터 저장 완료:', tasks.length);
    
    // 마지막 업데이트 시간: task의 상태 변경 시각 후보들 중 최대값
    const latest = tasks.reduce((acc, cur) => {
        const candidateTimes = Array.isArray(cur.data)
            ? cur.data.filter(Boolean)
            : [cur.updatedAt, cur.createdAt].filter(Boolean);
        const lastUpdated = candidateTimes.reduce((a, c) => (a && a > c) ? a : c, null);
        return (!acc || (lastUpdated && lastUpdated > acc)) ? lastUpdated : acc;
    }, null) || new Date().toISOString();
    await saveUpdateTime('tasks', latest);
};

// 작업 데이터 로드 (tasks)
export const loadTasks = async () => {
    const db = await initDB();
    const transaction = db.transaction(['tasks'], 'readonly');
    const store = transaction.objectStore('tasks');
    const all = await requestToPromise(store.getAll());
    return all;
};

// 작업 이력 데이터 저장 (works)
export const saveWorks = async (works) => {
    const db = await initDB();
    const transaction = db.transaction(['works'], 'readwrite');
    const store = transaction.objectStore('works');
    
    await requestToPromise(store.clear());
    await Promise.all(works.map(work => requestToPromise(store.put(work))));
    await waitTransactionComplete(transaction);
    
    console.log('[INDEXEDDB] 작업 이력 저장 완료:', works.length);
    
    const latest = works.reduce((acc, cur) => (!acc || (cur.createdAt && cur.createdAt > acc)) ? cur.createdAt : acc, null) || new Date().toISOString();
    await saveUpdateTime('works', latest);
};

// 작업 이력 데이터 로드 (works)
export const loadWorks = async () => {
    const db = await initDB();
    const transaction = db.transaction(['works'], 'readonly');
    const store = transaction.objectStore('works');
    const all = await requestToPromise(store.getAll());
    return all;
};

// 로그 데이터 로드
export const loadLogs = async () => {
    const db = await initDB();
    const transaction = db.transaction(['logs'], 'readonly');
    const store = transaction.objectStore('logs');
    const logs = await requestToPromise(store.getAll());
    return logs;
};

// 마지막 업데이트 시간 저장
const saveUpdateTime = async (dataType, timestamp) => {
    const db = await initDB();
    const transaction = db.transaction(['updateTimes'], 'readwrite');
    const store = transaction.objectStore('updateTimes');
    
    await requestToPromise(store.put({
        dataType,
        timestamp,
        updatedAt: new Date().toISOString()
    }));
    await waitTransactionComplete(transaction);
};

// 마지막 업데이트 시간 로드
export const getUpdateTime = async (dataType) => {
    const db = await initDB();
    const transaction = db.transaction(['updateTimes'], 'readonly');
    const store = transaction.objectStore('updateTimes');
    const result = await requestToPromise(store.get(dataType));
    return result ? result.timestamp : null;
};

// 7일 이전 로그 데이터 정리
export const cleanupOldLogs = async () => {
    const db = await initDB();
    const transaction = db.transaction(['logs'], 'readwrite');
    const store = transaction.objectStore('logs');
    const index = store.index('createdAt');

    // 7일 이전 날짜 계산
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
    const cutoffDate = sevenDaysAgo.toISOString();

    // 7일 이전 데이터 조회 및 삭제
    const range = IDBKeyRange.upperBound(cutoffDate);
    const request = index.openCursor(range);

    let deletedCount = 0;

    request.onsuccess = (event) => {
        const cursor = event.target.result;
        if (cursor) {
            cursor.delete();
            deletedCount++;
            cursor.continue();
        } else {
            console.log(`[INDEXEDDB] 7일 이전 로그 ${deletedCount}개 정리 완료`);
        }
    };
};

// 앱 시작 시 IndexedDB 초기화
export const initializeDB = async () => {
    await initDB();
    console.log('[INDEXEDDB] 초기화 완료');
    
    // 7일 이전 데이터 정리
    await cleanupOldLogs();
};
